// Dettaglio ticket con tabs: Info / Timeline / Chat / Allegati
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit, AlertTriangle, MapPin, Building2, CalendarDays, Pencil, Check, X } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../../lib/api'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Tabs, TabsContent } from '../../components/ui/Tabs'
import { StatusPill } from '../../components/ui/StatusPill'
import { PriorityIndicator } from '../../components/ui/PriorityIndicator'
import { Badge } from '../../components/ui/Badge'
import { Card } from '../../components/ui/Card'
import { Alert } from '../../components/ui/Alert'
import { Skeleton } from '../../components/ui/Skeleton'
import { TicketTimeline } from './components/TicketTimeline'
import { TicketChat } from './components/TicketChat'
import { TicketAttachments } from './components/TicketAttachments'
import { TicketAssignment } from './components/TicketAssignment'
import { SLAIndicator } from './components/SLAIndicator'
import { ContextualHelp } from '../../components/shared/ContextualHelp'
import { useTicket, useUpdateTicket } from '../../hooks/useTickets'
import { formatDate } from '../../lib/utils'

const TABS = [
  { value: 'details', label: 'Dettagli' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'chat', label: 'Chat' },
  { value: 'attachments', label: 'Allegati' },
]

function SchedulingCard({ ticket, ticketId }: { ticket: { scheduledStart?: string | null; scheduledEnd?: string | null }; ticketId: string }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    scheduledStart: ticket.scheduledStart ? ticket.scheduledStart.slice(0, 16) : '',
    scheduledEnd: ticket.scheduledEnd ? ticket.scheduledEnd.slice(0, 16) : '',
  })

  const reschedule = useMutation({
    mutationFn: (data: { scheduledStart: string; scheduledEnd: string }) =>
      api.patch(`/tickets/${ticketId}/reschedule`, {
        scheduledStart: new Date(data.scheduledStart).toISOString(),
        scheduledEnd: new Date(data.scheduledEnd).toISOString(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ticket', ticketId] })
      void qc.invalidateQueries({ queryKey: ['tickets'] })
      void qc.invalidateQueries({ queryKey: ['tickets-calendar'] })
      setEditing(false)
    },
  })

  const canSave = form.scheduledStart && form.scheduledEnd && form.scheduledEnd > form.scheduledStart

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[var(--fc-foreground)] flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-[var(--fc-primary)]" />
          Pianificazione
        </h3>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)] hover:bg-[var(--fc-surface-overlay)]"
          >
            <Pencil className="h-3 w-3" /> Modifica
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Inizio pianificato</label>
              <input
                type="datetime-local"
                value={form.scheduledStart}
                onChange={e => setForm(p => ({ ...p, scheduledStart: e.target.value }))}
                className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Fine pianificata</label>
              <input
                type="datetime-local"
                value={form.scheduledEnd}
                onChange={e => setForm(p => ({ ...p, scheduledEnd: e.target.value }))}
                className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
              />
            </div>
          </div>
          {reschedule.isError && (
            <p className="text-xs text-red-600">Errore durante il salvataggio.</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => reschedule.mutate(form)}
              disabled={!canSave || reschedule.isPending}
              className="flex items-center gap-1.5 rounded-lg bg-[var(--fc-primary)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" /> {reschedule.isPending ? 'Salvando...' : 'Salva'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="flex items-center gap-1.5 rounded-lg border border-[var(--fc-border)] px-3 py-1.5 text-xs"
            >
              <X className="h-3.5 w-3.5" /> Annulla
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs text-[var(--fc-foreground-muted)] mb-0.5">Inizio pianificato</p>
            <p className="text-[var(--fc-foreground)]">
              {ticket.scheduledStart ? formatDate(ticket.scheduledStart, 'dd/MM/yyyy HH:mm') : (
                <span className="text-[var(--fc-foreground-muted)] italic">Non impostato</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-[var(--fc-foreground-muted)] mb-0.5">Fine pianificata</p>
            <p className="text-[var(--fc-foreground)]">
              {ticket.scheduledEnd ? formatDate(ticket.scheduledEnd, 'dd/MM/yyyy HH:mm') : (
                <span className="text-[var(--fc-foreground-muted)] italic">Non impostato</span>
              )}
            </p>
          </div>
        </div>
      )}
    </Card>
  )
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('details')

  const { data: ticket, isLoading, error } = useTicket(id ?? '')
  const updateMutation = useUpdateTicket(id ?? '')

  const { data: company } = useQuery({
    queryKey: ['company', ticket?.companyId],
    queryFn: () => api.get<{ id: string; name: string }>(`/companies/${ticket!.companyId}`).then(r => r.data),
    enabled: Boolean(ticket?.companyId),
  })

  const { data: site } = useQuery({
    queryKey: ['site', ticket?.siteId],
    queryFn: () => api.get<{ id: string; name: string; address?: string }>(`/sites/${ticket!.siteId}`).then(r => r.data),
    enabled: Boolean(ticket?.siteId),
  })

  if (error) {
    return (
      <Alert variant="error" title="Errore">
        Impossibile caricare il ticket. Verificare la connessione.
      </Alert>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          isLoading
            ? 'Caricamento...'
            : ticket ? `Ticket ${ticket.ticketNumber}` : 'Ticket'
        }
        breadcrumb={[
          { label: 'Ticket', href: '/tickets' },
          { label: ticket ? ticket.ticketNumber : '...' },
        ]}
        actions={
          ticket && (
            <div className="flex gap-2">
              {ticket.status !== 'escalated' && (
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<AlertTriangle className="h-4 w-4 text-red-500" />}
                  onClick={() => updateMutation.mutate({ status: 'escalated' })}
                  isLoading={updateMutation.isPending}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  Urgente
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Edit className="h-4 w-4" />}
                onClick={() => navigate(`/tickets/${id}/edit`)}
              >
                Modifica
              </Button>
            </div>
          )
        }
      />

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton height={120} className="rounded-lg" />
          <Skeleton height={48} className="rounded-lg" />
          <Skeleton height={200} className="rounded-lg" />
        </div>
      ) : !ticket ? (
        <Alert variant="error">Ticket non trovato</Alert>
      ) : (
        <>
          {/* Intestazione ticket */}
          <Card>
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <StatusPill status={ticket.status} />
                  <PriorityIndicator priority={ticket.priority} />
                  {ticket.slaDeadline && (
                    <SLAIndicator deadline={ticket.slaDeadline} />
                  )}
                </div>
                <h2 className="text-lg font-semibold text-[var(--fc-foreground)]">{ticket.title}</h2>
                {ticket.description && (
                  <p className="mt-2 text-sm text-[var(--fc-foreground-muted)]">{ticket.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                <div>
                  <p className="text-xs text-[var(--fc-foreground-subtle)] mb-0.5">Tipo</p>
                  <Badge variant="default">{ticket.type}</Badge>
                </div>
                <div>
                  <p className="text-xs text-[var(--fc-foreground-subtle)] mb-0.5">Cliente</p>
                  <div className="flex items-center gap-1 text-[var(--fc-foreground)]">
                    <Building2 className="h-3 w-3" aria-hidden="true" />
                    {company?.name ?? ticket.companyId?.slice(0, 8) ?? 'N/D'}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-[var(--fc-foreground-subtle)] mb-0.5">Sito</p>
                  <div className="flex items-center gap-1 text-[var(--fc-foreground)]">
                    <MapPin className="h-3 w-3" aria-hidden="true" />
                    {site?.name ?? ticket.siteId?.slice(0, 8) ?? 'N/D'}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-[var(--fc-foreground-subtle)] mb-0.5">Creato</p>
                  <span className="text-[var(--fc-foreground)]">{formatDate(ticket.createdAt)}</span>
                </div>
                {ticket.slaDeadline && (
                  <div>
                    <p className="text-xs text-[var(--fc-foreground-subtle)] mb-0.5">Scadenza SLA</p>
                    <span className="text-[var(--fc-foreground)]">{formatDate(ticket.slaDeadline)}</span>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Assegnazione */}
          <Card>
            <h3 className="mb-3 text-sm font-semibold text-[var(--fc-foreground)]">Assegnazione tecnico</h3>
            <TicketAssignment ticket={ticket} />
          </Card>

          {/* Pianificazione */}
          <SchedulingCard ticket={ticket} ticketId={ticket.id} />

          {/* Tabs */}
          <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden">
            <Tabs tabs={TABS} value={activeTab} onValueChange={setActiveTab}>
              <TabsContent value="details" className="p-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="mb-2 text-sm font-semibold text-[var(--fc-foreground)]">Descrizione completa</h4>
                    <p className="text-sm text-[var(--fc-foreground-muted)]">
                      {ticket.description || 'Nessuna descrizione aggiuntiva'}
                    </p>
                  </div>
                  {ticket.internalNotes && (
                    <div>
                      <h4 className="mb-2 text-sm font-semibold text-[var(--fc-foreground)]">Device Guasto</h4>
                      <p className="text-sm text-[var(--fc-foreground-muted)]">{ticket.internalNotes}</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="timeline" className="p-4">
                <TicketTimeline ticketId={ticket.id} />
              </TabsContent>

              <TabsContent value="chat">
                <TicketChat ticketId={ticket.id} />
              </TabsContent>

              <TabsContent value="attachments" className="p-4">
                <TicketAttachments ticketId={ticket.id} />
              </TabsContent>
            </Tabs>
          </div>
        </>
      )}

      <ContextualHelp />
    </div>
  )
}
